import { computed } from 'vue';
import { defineStore } from 'pinia';
import type { HipSpineLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import {
    diffLandmarksForSave,
    buildPosturePutBody,
    postureFieldsChanged,
    type LandmarkSavePayload,
    type PosturePutBody,
    type ResolvedPostureFields,
} from '@/domain/anatomy/hipSpinePostureSave';
import { swapAnteriorPosteriorLandmarks } from '@/domain/anatomy/hipSpineLandmarks';
import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
import { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';

export type PostureSavePlan = {
    postureId: string;
    /** Landmarks to PUT; empty means no landmark save needed. */
    landmarkPayload: LandmarkSavePayload[];
    /** Posture editable-fields body to PUT; null means no posture save needed. */
    postureBody: PosturePutBody | null;
};

export const useHipSpinePostureSavePlan = defineStore('hip-spine-posture-save-plan', () => {
    const store = useHipSpineStore();
    const editVm = useHipSpineXRayEditViewModel();

    /**
     * The posture's full landmark set as it should be PERSISTED: edits overlaid,
     * and the anterior/posterior designation baked in when the viewed side differs
     * from native. NOT lumbar-pruned (we persist every landmark, not just the
     * displayed pair).
     */
    function orientedLandmarksForSave(postureId: string): HipSpineLandmarkApiObject[] {
        const edited = editVm.editedLandmarks(postureId);
        return editVm.isSwapped(postureId) ? swapAnteriorPosteriorLandmarks(edited) : edited;
    }

    function resolvedPostureFields(postureId: string): ResolvedPostureFields {
        return {
            side: editVm.viewedSide(postureId),
            lumbarLevel: editVm.selectedLumbarLevel(postureId),
            femur1: editVm.resolvedFemurRadius(postureId, 1),
            femur2: editVm.resolvedFemurRadius(postureId, 2),
        };
    }

    /** Build the save plan (what, if anything, must be PUT) for a posture. */
    function buildSavePlan(postureId: string): PostureSavePlan {
        const viewState = store.viewState(postureId);
        const serverPosture = store.hipSpinePostures.find((p) => p.posture === postureId);
        const serverLandmarks = viewState?.landmarks ?? [];
        const landmarkPayload = diffLandmarksForSave(
            orientedLandmarksForSave(postureId),
            serverLandmarks,
        );
        const fields = resolvedPostureFields(postureId);
        const postureBody =
            serverPosture && postureFieldsChanged(fields, serverPosture)
                ? buildPosturePutBody(fields)
                : null;
        return { postureId, landmarkPayload, postureBody };
    }

    /** A posture is dirty when it has any landmark change or any editable-field change. */
    function isPostureDirty(postureId: string): boolean {
        const plan = buildSavePlan(postureId);
        return plan.landmarkPayload.length > 0 || plan.postureBody !== null;
    }

    const dirtyPostureIds = computed(() =>
        store.hipSpinePostures.map((p) => p.posture).filter((id) => isPostureDirty(id)),
    );

    return { buildSavePlan, isPostureDirty, dirtyPostureIds };
});
